import { forwardRef, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import moment from 'moment';
import { Message } from '~/schemas/message';

interface BubblesProps {
  messages: Message[];
  t: any;
}

const Bubbles = forwardRef<HTMLDivElement, BubblesProps>(
  ({ messages, t }, ref) => {
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [selectedCaption, setSelectedCaption] = useState<string | null>(null);
    const dialogRef = useRef<HTMLDialogElement>(null);

    // Function to open the dialog
    const openImageDialog = (imageUrl: string, caption?: string) => {
      setSelectedImage(imageUrl);
      setSelectedCaption(caption || null);
      // Use the dialog's showModal method
      if (dialogRef.current) {
        dialogRef.current.showModal();
      }
    };

    // Function to close the dialog
    const closeImageDialog = () => {
      setSelectedImage(null);
      setSelectedCaption(null);
      if (dialogRef.current) {
        dialogRef.current.close();
      }
    };

    if (!messages.length)
      return (
        <div className="text-md text-base-content flex h-full w-full items-center justify-center opacity-70">
          {t('chat_dashboard.no_messages')}
        </div>
      );

    return (
      <>
        <AnimatePresence initial={false}>
          {messages.map(
            (
              { id, message, actorType, created_at, media }: Message,
              msgIndex,
            ) => (
              <motion.div
                ref={msgIndex === messages.length - 1 ? ref : null}
                key={`id-${msgIndex}`}
                className={`chat ${
                  actorType === 'USER' ? 'chat-start' : 'chat-end'
                } flex flex-col gap-2 text-sm`}
                initial={{ opacity: 0, scale: 0.8, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, height: 0 }}
                transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 30,
                  opacity: { duration: 0.2 },
                }}
              >
                <motion.div
                  className={`text-balance chat-bubble ${
                    actorType === 'USER'
                      ? 'bg-primary text-primary-content'
                      : actorType === 'HUMAN_AGENT'
                      ? 'bg-green-200 '
                      : 'bg-base-200 text-base-content'
                  }`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.1 }}
                >
                  {/* ... Media rendering logic ... */}
                  {media && media.type === 'image' ? (
                    <div className="flex flex-col gap-1">
                      <div className="flex w-full justify-center">
                        <img
                          src={media?.url || ''}
                          alt={media.caption}
                          className="h-48 w-48 cursor-pointer rounded-lg object-cover"
                          onClick={() =>
                            openImageDialog(media?.url!, media.caption)
                          }
                        />
                      </div>
                      {media.caption ? (
                        <p className="text-sm italic">{media.caption}</p>
                      ) : message ? (
                        <p className="text-sm">{message}</p>
                      ) : null}
                    </div>
                  ) : media && media.type === 'audio' ? (
                    <div className="flex flex-col gap-1">
                      <audio controls>
                        <source src={media?.url} type="audio/ogg" />
                        Your browser does not support the audio element.
                      </audio>
                    </div>
                  ) : media && media.type === 'ig_reel' ? ( // Handle ig_reel type
                    <div className="flex flex-col gap-1">
                      <video
                        controls
                        className="mx-auto max-w-[150px] rounded-lg"
                      >
                        {' '}
                        {/* Added mx-auto to center */}
                        <source src={media?.url} type="video/mp4" />{' '}
                        {/* Assuming MP4, adjust if needed */}
                        Your browser does not support the video tag.
                      </video>
                      {media.caption && (
                        <p className="text-sm italic">{media.caption} </p>
                      )}
                    </div>
                  ) : (media && media.type === null) || !media ? (
                    message
                  ) : (
                    <>
                      <div className="flex items-center rounded border bg-gray-50 p-3 dark:bg-gray-700">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="mr-3 h-8 w-8 text-gray-500 dark:text-gray-300"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V7a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        <div>
                          <p className="truncate text-sm font-medium">
                            {media?.fileName || 'File'}
                          </p>
                          <a
                            href={media?.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-1 inline-block text-xs text-blue-500 hover:underline"
                          >
                            Download
                          </a>
                        </div>
                      </div>
                      {media?.caption && (
                        <p className="text-sm italic">{media.caption}</p>
                      )}
                    </>
                  )}

                  {/* Add Instagram origin notice */}
                  {media?.origin === 'instagram' && (
                    <p className="mt-1 text-xs italic opacity-70">
                      posted on Instagram
                    </p>
                  )}
                </motion.div>

                <motion.time
                  className="chat-footer text-xs opacity-50"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.5 }}
                  transition={{ delay: 0.2 }}
                >
                  {moment(created_at).format('MMM DD YYYY, h:mm a')}
                </motion.time>
              </motion.div>
            ),
          )}
        </AnimatePresence>

        {/* Image Dialog */}
        <dialog
          ref={dialogRef}
          className="modal backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              closeImageDialog();
            }
          }}
        >
          <div className="modal-box bg-base-100 max-w-4xl overflow-hidden rounded-lg p-1 shadow-xl">
            {selectedImage && (
              <div className="relative">
                <img
                  src={selectedImage}
                  alt="Enlarged view"
                  className="h-auto max-h-[80vh] w-full object-contain"
                />
                {selectedCaption && (
                  <p className="text-base-content mt-2 px-4 text-center text-sm italic md:text-base">
                    {selectedCaption}
                  </p>
                )}
                <button
                  className="btn btn-sm btn-circle bg-base-200 text-base-content absolute right-2 top-2"
                  onClick={closeImageDialog}
                >
                  ✕
                </button>
              </div>
            )}
          </div>
        </dialog>
      </>
    );
  },
);

export default Bubbles;
